// Calculates and displays the expected delivery date and order cut-off countdown for in-stock products.
// Cut-off hour, transit days, and excluded dates are read from data attributes set in product-delivery-information.liquid,
// which reads from the Country metaobject matched by the customer's country code.
// Excluded dates are passed as a comma-separated YYYY-MM-DD string.

if (!customElements.get('delivery-estimation')) {
  class DeliveryEstimation extends HTMLElement {
    constructor() {
      super();

      this.deliveryDayEl = this.querySelector('.js-delivery-day');
      this.countdownTimerEl = this.querySelector('.js-delivery-time');
      this.cutoffHour = parseInt(this.dataset.cutoffHour, 10) || 12;
      this.transitDays = parseInt(this.dataset.transitDays, 10) || 1;
      this.excludedDates = this.dataset.excluded
        ? this.dataset.excluded.split(',').filter(d => d.trim())
        : [];

      this.renderDeliveryInfo();
      this.interval = setInterval(this.renderDeliveryInfo.bind(this), 1000);
    }

    // Clears the countdown interval when the element is removed from the DOM
    disconnectedCallback() {
      clearInterval(this.interval);
    }

    getCutoffTime() {
      const cutoff = new Date();
      cutoff.setHours(this.cutoffHour, 0, 0, 0);
      return cutoff;
    }

    // The next day an order can actually be dispatched: today if we're still before the cut-off on a
    // working day, otherwise the next working day that isn't an excluded date.
    getDispatchDate(now) {
      const cutoff = this.getCutoffTime();
      const today = now.getDay();
      const dispatchDate = new Date(now);
      const isWeekday = today >= 1 && today <= 5;

      if (!(isWeekday && now < cutoff)) {
        let daysToAdd = 1;
        if (today === 5) daysToAdd = 3; // Friday after cutoff → Monday
        else if (today === 6) daysToAdd = 2; // Saturday → Monday
        dispatchDate.setDate(dispatchDate.getDate() + daysToAdd);
      }

      return this.skipExcludedDates(dispatchDate);
    }

    renderDeliveryInfo() {
      const now = new Date();
      // Count down to the cut-off on the dispatch day, so the countdown and the delivery day
      // below it always refer to the same order. Recalculated each tick so both roll over
      // together when the cut-off passes with the page still open.
      const dispatchDate = this.getDispatchDate(now);
      const targetCutoff = new Date(dispatchDate);
      targetCutoff.setHours(this.cutoffHour, 0, 0, 0);
      const distance = Math.max(targetCutoff.getTime() - now.getTime(), 0);

      // Over a weekend or excluded run the cut-off can be more than a day away, so show days too
      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));

      const parts = [];
      if (days >= 1) parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);
      if (days >= 1 || hours >= 1) parts.push(`${hours} ${hours === 1 ? 'hr' : 'hrs'}`);
      parts.push(`${minutes} ${minutes === 1 ? 'min' : 'mins'}`);
      const countdown = parts.join(' ');

      if (this.countdownTimerEl) this.countdownTimerEl.textContent = countdown;
      if (this.deliveryDayEl) this.deliveryDayEl.textContent = this.getDeliveryDay(dispatchDate);
    }

    getDeliveryDay(dispatchDate) {
      let deliveryDate = new Date(dispatchDate);
      let daysAdded = 0;

      while (daysAdded < this.transitDays) {
        deliveryDate.setDate(deliveryDate.getDate() + 1);
        const dow = deliveryDate.getDay();
        if (dow !== 0 && dow !== 6) daysAdded++;
      }

      deliveryDate = this.skipExcludedDates(deliveryDate);

      const day = new Intl.DateTimeFormat('en-GB', { weekday: 'long' }).format(deliveryDate);
      const month = new Intl.DateTimeFormat('en-GB', { month: 'long' }).format(deliveryDate);
      return `${day} ${deliveryDate.getDate()} ${month}`;
    }

    // Loops until the date lands on a non-excluded working day.
    // Loops rather than a single pass to handle consecutive excluded dates (e.g. Christmas + Boxing Day).
    skipExcludedDates(date) {
      let result = new Date(date);
      let dateSkipped = true;

      while (dateSkipped) {
        dateSkipped = false;
        for (const entry of this.excludedDates) {
          const [year, month, day] = entry.trim().split('-').map(Number);
          if (
            result.getDate() === day &&
            result.getMonth() + 1 === month &&
            result.getFullYear() === year
          ) {
            result.setDate(result.getDate() + 1);
            while (result.getDay() === 0 || result.getDay() === 6) {
              result.setDate(result.getDate() + 1);
            }
            dateSkipped = true;
            break;
          }
        }
      }

      return result;
    }
  }

  customElements.define('delivery-estimation', DeliveryEstimation);
}
